import { computed, defineComponent, getCurrentInstance, nextTick, ref, watch, watchEffect, } from 'vue';
import ClearableLabeledInput from './ClearableLabeledInput';
import ResizableTextArea from './ResizableTextArea';
import { textAreaProps } from './inputProps';
import { fixControlledValue, resolveOnChange, triggerFocus } from './Input';
import classNames from '../_util/classNames';
import { useInjectFormItemContext } from '../form/FormItemContext';
import useConfigInject from '../_util/hooks/useConfigInject';
import omit from '../_util/omit';
function fixEmojiLength(value, maxLength) {
    return [...(value || '')].slice(0, maxLength).join('');
}
function setTriggerValue(isCursorInEnd, preValue, triggerValue, maxLength) {
    let newTriggerValue = triggerValue;
    if (isCursorInEnd) {
        // 光标在尾部，直接截断
        newTriggerValue = fixEmojiLength(triggerValue, maxLength);
    }
    else if ([...(preValue || '')].length < triggerValue.length &&
        [...(triggerValue || '')].length > maxLength) {
        // 光标在中间，如果最后的值超过最大值，则采用原先的值
        newTriggerValue = preValue;
    }
    return newTriggerValue;
}
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'WdTextarea',
    inheritAttrs: false,
    props: textAreaProps(),
    setup(props, { attrs, expose, emit }) {
        const formItemContext = useInjectFormItemContext();
        const stateValue = ref(props.value === undefined ? props.defaultValue : props.value);
        const resizableTextArea = ref();
        const textAreaScroll = ref(false);
        const mergedValue = ref('');
        const { prefixCls, size, direction } = useConfigInject('input', props);
        const showCount = computed(() => {
            return props.showCount === '' || props.showCount || false;
        });
        // Max length value
        const hasMaxLength = computed(() => Number(props.maxlength) > 0);
        const compositing = ref(false);
        const oldCompositionValueRef = ref();
        const oldSelectionStartRef = ref(0);
        const onInternalCompositionStart = (e) => {
            compositing.value = true;
            // 拼音输入前保存一份旧值
            oldCompositionValueRef.value = mergedValue.value;
            // 保存旧的光标位置
            oldSelectionStartRef.value = e.currentTarget.selectionStart;
            emit('compositionstart', e);
        };
        const onInternalCompositionEnd = (e) => {
            compositing.value = false;
            let triggerValue = e.currentTarget.value;
            if (hasMaxLength.value) {
                const isCursorInEnd = oldSelectionStartRef.value >= props.maxlength + 1 ||
                    oldSelectionStartRef.value === oldCompositionValueRef.value?.length;
                triggerValue = setTriggerValue(isCursorInEnd, oldCompositionValueRef.value, triggerValue, props.maxlength);
            }
            // Patch composition onChange when value changed
            if (triggerValue !== mergedValue.value) {
                setValue(triggerValue);
                resolveOnChange(e.currentTarget, e, triggerChange, triggerValue);
            }
            emit('compositionend', e);
        };
        const instance = getCurrentInstance();
        watch(() => props.value, () => {
            if ('value' in instance.vnode.props || {}) {
                stateValue.value = props.value ?? '';
            }
        });
        const focus = (option) => {
            triggerFocus(resizableTextArea.value?.textArea, option);
        };
        const blur = () => {
            resizableTextArea.value?.textArea?.blur();
        };
        const setValue = (value, callback) => {
            if (stateValue.value === value) {
                return;
            }
            if (props.value === undefined) {
                stateValue.value = value;
            }
            else {
                nextTick(() => {
                    if (resizableTextArea.value.textArea.value !== mergedValue.value) {
                        resizableTextArea.value?.instance.update?.();
                    }
                });
            }
            nextTick(() => {
                callback && callback();
            });
        };
        const handleKeyDown = (e) => {
            if (e.keyCode === 13) {
                emit('pressEnter', e);
            }
            emit('keydown', e);
        };
        const onBlur = e => {
            const { onBlur } = props;
            onBlur?.(e);
            formItemContext.onFieldBlur();
        };
        const triggerChange = (e) => {
            emit('update:value', e.target.value);
            emit('change', e);
            emit('input', e);
            formItemContext.onFieldChange();
        };
        const handleReset = (e) => {
            resolveOnChange(resizableTextArea.value.textArea, e, triggerChange);
            setValue('', () => {
                focus();
            });
        };
        const handleChange = (e) => {
            const { composing } = e.target;
            let triggerValue = e.target.value;
            compositing.value = !!(e.isComposing || composing);
            if ((compositing.value && props.lazy) || stateValue.value === triggerValue)
                return;
            if (hasMaxLength.value) {
                // 1. 复制粘贴超过maxlength的情况 2.未超过maxlength的情况
                const target = e.target;
                const isCursorInEnd = target.selectionStart >= props.maxlength + 1 ||
                    target.selectionStart === triggerValue.length ||
                    !target.selectionStart;
                triggerValue = setTriggerValue(isCursorInEnd, mergedValue.value, triggerValue, props.maxlength);
            }
            resolveOnChange(e.currentTarget, e, triggerChange, triggerValue);
            setValue(triggerValue);
            textAreaScroll.value =
                resizableTextArea.value?.textArea?.scrollHeight >
                    resizableTextArea.value?.textArea?.clientHeight;
        };
        const renderTextArea = () => {
            const { style, class: customClass } = attrs;
            const { bordered = true } = props;
            const resizeProps = {
                ...omit(props, ['allowClear']),
                ...attrs,
                style: showCount.value ? {} : style,
                class: {
                    [`${prefixCls.value}-borderless`]: !bordered,
                    [`${customClass}`]: customClass && !showCount.value,
                    [`${prefixCls.value}-sm`]: size.value === 'small',
                    [`${prefixCls.value}-lg`]: size.value === 'large',
                },
                showCount: null,
                prefixCls: prefixCls.value,
                onInput: handleChange,
                onChange: handleChange,
                onBlur,
                onKeydown: handleKeyDown,
                onCompositionstart: onInternalCompositionStart,
                onCompositionend: onInternalCompositionEnd,
            };
            if (props.valueModifiers?.lazy) {
                delete resizeProps.onInput;
            }
            return (<ResizableTextArea {...resizeProps} id={resizeProps.id ?? formItemContext.id.value} ref={resizableTextArea} maxlength={props.maxlength}/>);
        };
        expose({
            focus,
            blur,
            resizableTextArea,
        });
        watchEffect(() => {
            let val = fixControlledValue(stateValue.value);
            if (!compositing.value &&
                hasMaxLength.value &&
                (props.value === null || props.value === undefined)) {
                // fix #27612 将value转为数组进行截取，解决 '😂'.length === 2 等emoji表情导致的截取乱码的问题
                val = fixEmojiLength(val, props.maxlength);
            }
            mergedValue.value = val;
        });
        return () => {
            const { maxlength, bordered = true, hidden } = props;
            const { style, class: customClass } = attrs;
            const inputProps = {
                ...props,
                ...attrs,
                prefixCls: prefixCls.value,
                inputType: 'text',
                handleReset,
                direction: direction.value,
                bordered,
                style: showCount.value ? undefined : style,
            };
            let textareaNode = (<ClearableLabeledInput {...inputProps} value={mergedValue.value} v-slots={{ element: renderTextArea }}/>);
            if (showCount.value) {
                const valueLength = [...mergedValue.value].length;
                let dataCount = '';
                if (typeof showCount.value === 'object') {
                    dataCount = showCount.value.formatter({ count: valueLength, maxlength });
                }
                else {
                    dataCount = `${valueLength}${hasMaxLength.value ? `/${maxlength}` : ''}`;
                }
                textareaNode = (<div class={classNames(prefixCls.value, `${prefixCls.value}-textarea-wrapper-rtl`, {
                        [`${prefixCls.value}-textarea-scroll`]: textAreaScroll.value,
                        [`${prefixCls.value}-resize`]: props.resize,
                        [`${prefixCls.value}-disabled`]: props.disabled,
                        [`${prefixCls.value}-borderless`]: props.bordered === false,
                        [`${prefixCls.value}-xs`]: props.size === 'extra-small',
                        [`${prefixCls.value}-sm`]: props.size === 'small',
                        [`${prefixCls.value}-bg`]: props.size === 'big',
                        [`${prefixCls.value}-lg`]: props.size === 'large',
                    })}>
            <div hidden={hidden} class={classNames(`${prefixCls.value}-textarea`, {
                        [`${prefixCls.value}-textarea-rtl`]: direction.value === 'rtl',
                    }, customClass)} style={style}>
              {textareaNode}
            </div>
            <div class={classNames(`${prefixCls.value}-textarea-show-count`, {
                        [`${prefixCls.value}-textarea-count-max`]: valueLength === maxlength,
                    })}>
              {dataCount}
            </div>
          </div>);
            }
            return textareaNode;
        };
    },
});
